// Built-in sticker packs. Inline SVG strings keep previewUI portable (no CDN, no
// external deps) and identical between Dashboard and Next. A `preset` sticker stores
// only its `pack:key` reference in the DB (value), resolved here at render time, so the
// Page.Stickers JSON stays tiny. Dashboard imports STICKER_PACKS to render the picker.

const FUN = {
  star: '<svg viewBox="0 0 100 100" width="100%" height="100%"><path d="M50 5l13 27 30 4-22 21 5 30-26-14-26 14 5-30L7 36l30-4z" fill="#FFC93C" stroke="#E8A100" stroke-width="2" stroke-linejoin="round"/></svg>',
  heart:
    '<svg viewBox="0 0 100 100" width="100%" height="100%"><path d="M50 86C18 64 8 44 8 30 8 17 18 9 29 9c8 0 16 4 21 12 5-8 13-12 21-12 11 0 21 8 21 21 0 14-10 34-42 56z" fill="#FF4D6D"/></svg>',
  sparkle: '<svg viewBox="0 0 100 100" width="100%" height="100%"><path d="M50 6c4 24 16 36 40 44-24 8-36 20-40 44-4-24-16-36-40-44 24-8 36-20 40-44z" fill="#7C5CFC"/></svg>',
  fire: '<svg viewBox="0 0 100 100" width="100%" height="100%"><path d="M55 6c4 18-8 24-14 34-5 8-3 18 4 22-2-10 6-14 8-22 6 8 14 14 14 26 0 16-12 28-27 28S13 82 13 66c0-20 16-28 18-44 1-8 0-12 0-12 10 2 20 8 24 16 2-8 0-14 0-20z" fill="#FF7A1A"/></svg>',
  check:
    '<svg viewBox="0 0 100 100" width="100%" height="100%"><circle cx="50" cy="50" r="44" fill="#22C55E"/><path d="M30 52l13 13 27-30" fill="none" stroke="#fff" stroke-width="9" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  new: '<svg viewBox="0 0 100 100" width="100%" height="100%"><path d="M50 4l9 12 15-5-2 16 15 7-12 11 7 15-16-1-5 15-11-11-11 11-5-15-16 1 7-15-12-11 15-7-2-16 15 5z" fill="#D7FF3E" stroke="#9bbf00" stroke-width="2"/><text x="50" y="58" font-size="20" font-family="Arial, sans-serif" font-weight="700" text-anchor="middle" fill="#1a1a1a">NEW</text></svg>',
};

const DECO = {
  arrow:
    '<svg viewBox="0 0 100 100" width="100%" height="100%"><path d="M20 70c10-34 36-44 56-44m0 0-16-8m16 8-8 16" fill="none" stroke="#FF9F1C" stroke-width="9" stroke-linecap="round" stroke-linejoin="round"/></svg>',
  crown:
    '<svg viewBox="0 0 100 100" width="100%" height="100%"><path d="M15 75l-6-40 22 16 19-30 19 30 22-16-6 40z" fill="#FFC93C" stroke="#E8A100" stroke-width="2" stroke-linejoin="round"/><rect x="15" y="75" width="70" height="10" rx="3" fill="#E8A100"/></svg>',
  flower:
    '<svg viewBox="0 0 100 100" width="100%" height="100%"><g fill="#FF6FB5"><circle cx="50" cy="22" r="16"/><circle cx="78" cy="50" r="16"/><circle cx="50" cy="78" r="16"/><circle cx="22" cy="50" r="16"/></g><circle cx="50" cy="50" r="14" fill="#FFD23F"/></svg>',
  bubble:
    '<svg viewBox="0 0 100 100" width="100%" height="100%"><path d="M14 16h72a8 8 0 0 1 8 8v34a8 8 0 0 1-8 8H48l-20 18v-18h-14a8 8 0 0 1-8-8V24a8 8 0 0 1 8-8z" fill="#4EA8FF"/><text x="50" y="50" font-size="22" font-family="Arial, sans-serif" font-weight="700" text-anchor="middle" fill="#fff">Hi!</text></svg>',
  lightning:
    '<svg viewBox="0 0 100 100" width="100%" height="100%"><path d="M58 6 24 56h22l-8 38 38-54H52z" fill="#FFD23F" stroke="#E8A100" stroke-width="2" stroke-linejoin="round"/></svg>',
  rainbow:
    '<svg viewBox="0 0 100 100" width="100%" height="100%"><g fill="none" stroke-width="9"><path d="M12 78a38 38 0 0 1 76 0" stroke="#FF5A5F"/><path d="M24 78a26 26 0 0 1 52 0" stroke="#FFB400"/><path d="M36 78a14 14 0 0 1 28 0" stroke="#4EA8FF"/></g></svg>',
};

export const STICKER_PACKS = [
  { key: 'fun', name: 'Fun', stickers: Object.keys(FUN).map((key) => ({ key, value: `fun:${key}`, svg: FUN[key] })) },
  { key: 'deco', name: 'Deco', stickers: Object.keys(DECO).map((key) => ({ key, value: `deco:${key}`, svg: DECO[key] })) },
];

const PRESET_MAP = { ...Object.fromEntries(Object.entries(FUN).map(([k, v]) => [`fun:${k}`, v])), ...Object.fromEntries(Object.entries(DECO).map(([k, v]) => [`deco:${k}`, v])) };

// value is a "pack:key" reference (e.g. "fun:star"). Returns the inline SVG string or null.
export const getPresetSvg = (value) => PRESET_MAP[value] || null;

const STICKER_KINDS = ['emoji', 'preset', 'image'];

// Accepts an array OR a JSON string (the DB stores Page.Stickers as a JSON string).
// Returns a clean array of valid stickers with sane defaults so render/transform code
// never has to null-check individual fields.
export const normalizeStickers = (raw) => {
  let list = raw;
  if (typeof raw === 'string') {
    try {
      list = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(list)) return [];

  return list
    .filter((s) => s && STICKER_KINDS.includes(s.kind) && s.value)
    .map((s) => ({
      id: String(s.id),
      kind: s.kind,
      value: s.value,
      x: typeof s.x === 'number' ? s.x : 50,
      y: typeof s.y === 'number' ? s.y : 100,
      size: typeof s.size === 'number' ? s.size : 18,
      rotation: typeof s.rotation === 'number' ? s.rotation : 0,
      flipX: !!s.flipX,
      opacity: typeof s.opacity === 'number' ? s.opacity : 1,
      // Optional click-through link (the author can attach a URL the sticker navigates to).
      link: typeof s.link === 'string' && s.link ? s.link : null,
      // Stacking order within the layer (higher = on top). Default 0; new stickers sit on top via DOM order.
      z: typeof s.z === 'number' ? s.z : 0,
    }));
};
